// Angular
import { BehaviorSubject, Observable, takeUntil } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Libraries

import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';

// Modules
import { AppCleanup } from '../../../shared/models';

// Services
import { CoreService } from '../../../shared/services/core.service';

// Models
import { Channel } from '../models';
import { UserChannelViewModel } from '../models/user-channel-view.model';
import { CompanyChannelViewModel } from '../models/company-channel-view.model';
import { UserChannelUpdateModel } from '../models/user-channel-update.model';

// Store
import { AuthState } from '../../auth/state/auth.state';

// Enums
import { StatusType } from '../../../shared/enums';

@Injectable({
	providedIn: 'root',
})
export class ChannelService extends AppCleanup {
	@Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
	channels: Channel[] = [];
	protected channelsReadyVar: BehaviorSubject<Channel[]> = new BehaviorSubject<Channel[]>(null);
	channelsReady$ = this.channelsReadyVar.asObservable();

	protected userChannelsVar: BehaviorSubject<UserChannelViewModel[]> = new BehaviorSubject<UserChannelViewModel[]>(null);
	userChannels$ = this.userChannelsVar.asObservable();

	channelUrl: string;
	loadingChartData: boolean = false;

	constructor(
		private http: HttpClient,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
		this.channelUrl = `${this.apiUrl}api/channel`;
		this.initChannels();
	}

	initChannels() {
		this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
			// No need for channels list to initialize on links
			if (isLoggedIn) {
				this._getChannels()
					.pipe(takeUntil(this.destroy))
					.subscribe((channels) => {
						if (channels) {
							this.channels = channels;
							this.channelsReadyVar.next(this.channels);
						}
					});

				this.getUserDefaultChannels()
					.pipe(takeUntil(this.destroy))
					.subscribe((userChannels) => {
						this.userChannelsVar.next(userChannels);
					});
			}
		});
	}

	private _getChannels(): Observable<Array<Channel>> {
		return this.http.get<Array<Channel>>(this.channelUrl);
	}

	getChannels(): Observable<Array<Channel>> {
		return this.http.get<Array<Channel>>(this.channelUrl).pipe();
	}

	getChannel(key: string): Observable<Channel> {
		return this.http.get<Channel>(`${this.channelUrl}/${key}`);
	}

	createChannel(channel: Channel) {
		this._createChannel(channel)
			.pipe(takeUntil(this.destroy))
			.subscribe((c) => {
				if (c) {
					this.addChannelRecord(channel);
				}
			});
	}

	addChannelRecord(channel: Channel, showToaster: boolean = true) {
		if (this.channels) {
			this.channels.push(channel);
		}

		if (showToaster) {
			const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
			this.coreService.toast({ message: message, type: StatusType.SUCCESS });
		}

		this.channelsReadyVar.next(this.channels);
	}

	private _createChannel(channel: Channel): Observable<Channel> {
		return this.http.post<Channel>(`${this.channelUrl}`, channel);
	}

	modifyChannelRecord(_channel: Channel, showToaster: boolean = true) {
		const foundChannel: number = this.channels.findIndex((channel) => channel.id === _channel.id);
		if (foundChannel >= 0) {
			this.channels[foundChannel] = _channel;
			if (showToaster) {
				const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
				this.coreService.toast({ message: message, type: StatusType.SUCCESS });
			}
			this.channelsReadyVar.next(this.channels);
		} else {
			this.addChannelRecord(_channel, showToaster);
		}
	}

	updateChannel(channel: Channel) {
		return this.http.put<Channel>(`${this.channelUrl}/${channel.id}`, channel);
	}

	getCompanyDefaultChannels(id: number) {
		return this.http.get<CompanyChannelViewModel[]>(`${this.channelUrl}/company/${id}`);
	}

	getUserDefaultChannels() {
		return this.http.get<UserChannelViewModel[]>(`${this.channelUrl}/user`);
	}

	addUserDefaultChannel(item: UserChannelViewModel) {
		return this.http.post<UserChannelViewModel>(`${this.channelUrl}/user`, item);
	}

	updateUserDefaultChannel(item: UserChannelViewModel) {
		return this.http.put<UserChannelViewModel>(`${this.channelUrl}/user`, item);
	}

	updateUserDefaultChannels(item: UserChannelUpdateModel) {
		return this.http.put<UserChannelViewModel[]>(`${this.channelUrl}/userjobchannels`, item);
	}

	deleteUserDefaultChannel(id: number) {
		return this.http.delete<UserChannelViewModel>(`${this.channelUrl}/user/${id}`);
	}

	modifyUserChannelRecords(userChannels: UserChannelViewModel[]) {
		this.userChannelsVar.next(userChannels);
	}
}
